/**
 * Feed wizard lib — types + hooks for the 1D.5 feed wizard UI.
 *
 * Backing routes live in cmd/admin-bff/feeds.go. All paths are
 * nginx-prefixed at /api/v2.
 *
 * Query key convention:
 *   ["feeds", "list"]
 *   ["feeds", "source", slug]
 *   ["feeds", "source", slug, "samples"]
 *   ["feeds", "source", slug, "runs", page]
 *   ["feeds", "source", slug, "run", runId]
 *   ["feeds", "connectors"]
 *   ["feeds", "formats"]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

// Re-export the legacy list shape so existing imports still work.
export type { FeedSourceRecord } from "./iocs";
export { useFeedSources, useUpdateFeedSource } from "./iocs";

// ---------- Registry (connectors + formats) ----------

export interface ConnectorDescriptor {
    kind: string;
    displayName: string;
    description: string;
    requiresCredential: boolean;
    paginate?: boolean;
    streaming?: boolean;
    configSchema: JsonSchema;
}

export interface FormatDescriptor {
    kind: string;
    displayName: string;
    description: string;
    configSchema: JsonSchema;
}

export interface JsonSchema {
    type?: "string" | "integer" | "number" | "boolean" | "object" | "array";
    properties?: Record<string, JsonSchema>;
    required?: string[];
    enum?: (string | number)[];
    default?: unknown;
    description?: string;
    items?: JsonSchema;
    minimum?: number;
    maximum?: number;
    [k: string]: unknown;
}

export function useConnectors() {
    return useQuery({
        queryKey: ["feeds", "connectors"] as const,
        queryFn: () =>
            api
                .get<{ connectors: ConnectorDescriptor[] }>("/api/v2/connectors")
                .then((d) => d.connectors ?? []),
        staleTime: 5 * 60_000,
    });
}

export function useFormats() {
    return useQuery({
        queryKey: ["feeds", "formats"] as const,
        queryFn: () =>
            api
                .get<{ formats: FormatDescriptor[] }>("/api/v2/formats")
                .then((d) => d.formats ?? []),
        staleTime: 5 * 60_000,
    });
}

// ---------- Feed source detail ----------

export interface FeedMapping {
    target?: string;
    when?: string;
    drop_when?: string;
    fields?: Record<string, string>;
    source_values?: Record<string, string>;
}

export interface RunSummary {
    id: string;
    startedAt: string;
    finishedAt?: string;
    status: string; // "running" | "ok" | "partial" | "error"
    trigger: string; // "schedule" | "replay" | "manual"
    rowsIn: number;
    rowsOut: number;
    dropped: number;
    errors: number;
    bytes: number;
}

export interface RunError {
    rowIndex: number;
    field?: string;
    reason: string;
    excerpt?: string;
    stage: string;
}

export interface FeedSourceDetail {
    slug: string;
    name: string;
    connectorKind: string;
    formatKind: string;
    config: Record<string, unknown>;
    enabled: boolean;
    status: string;
    scheduleSeconds: number;
    weight: number;
    sampleRetentionDays: number;
    circuitBreakerThreshold: number;
    ingestWorkerConcurrency: number;
    consecutiveFailures: number;
    autoPaused: boolean;
    credentialRef?: string;
    tenantId?: string | null;
    mappingVersion: number;
    mappingDraft?: FeedMapping;
    mappingActive?: FeedMapping;
    lastRunAt?: string;
    lastStatus?: string;
    lastError: string;
    ingestedCount: number;
    runs: RunSummary[];
    recentErrors: RunError[];
}

export function useFeedSource(slug: string | null | undefined) {
    return useQuery({
        queryKey: ["feeds", "source", slug] as const,
        queryFn: () => api.get<FeedSourceDetail>(`/api/v2/feed-sources/${slug}`),
        enabled: !!slug,
        staleTime: 5_000,
    });
}

// ---------- Feed source CRUD ----------

export interface CreateFeedInput {
    slug: string;
    name: string;
    connectorKind: string;
    formatKind: string;
    config?: Record<string, unknown>;
    credentialRef?: string;
    scheduleSeconds?: number;
    weight?: number;
    sampleRetentionDays?: number;
    circuitBreakerThreshold?: number;
    ingestWorkerConcurrency?: number;
    tenantId?: string | null;
}

export function useCreateFeedSource() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateFeedInput) =>
            api.post<{ slug: string }>("/api/v2/feed-sources", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
            void qc.invalidateQueries({ queryKey: ["feeds"] });
        },
    });
}

export interface UpdateFeedDetailInput {
    name?: string;
    config?: Record<string, unknown>;
    credentialRef?: string;
    scheduleSeconds?: number;
    weight?: number;
    sampleRetentionDays?: number;
    circuitBreakerThreshold?: number;
    ingestWorkerConcurrency?: number;
    enabled?: boolean;
    mappingDraft?: FeedMapping;
}

/**
 * Detail-page sparse updater. Distinct from useUpdateFeedSource (which is the
 * legacy list-row mutator scoped to {weight, scheduleSeconds, enabled}).
 */
export function useUpdateFeedDetail(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: UpdateFeedDetailInput) =>
            api.patch<void>(`/api/v2/feed-sources/${slug}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug] });
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
        },
    });
}

export function useDeleteFeedSource() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (slug: string) => api.delete<void>(`/api/v2/feed-sources/${slug}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds"] });
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
        },
    });
}

// ---------- Samples ----------

export interface SampleMeta {
    id: string;
    capturedAt: string;
    byteCount: number;
    contentType: string;
    status: string; // "live" | "expired"
}

export interface SampleCaptureResult {
    id: string;
    byteCount: number;
    contentType: string;
    previewHex: string;
}

export function useFeedSamples(slug: string | null | undefined) {
    return useQuery({
        queryKey: ["feeds", "source", slug, "samples"] as const,
        queryFn: () =>
            api
                .get<{ samples: SampleMeta[] }>(`/api/v2/feed-sources/${slug}/samples`)
                .then((d) => d.samples ?? []),
        enabled: !!slug,
        staleTime: 5_000,
    });
}

export function useFetchSample(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () =>
            api.post<SampleCaptureResult>(`/api/v2/feed-sources/${slug}/sample`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug, "samples"] });
        },
    });
}

export function useDeleteSample(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            api.delete<void>(`/api/v2/feed-sources/${slug}/samples/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug, "samples"] });
        },
    });
}

// Fetch raw sample body as text (for the viewer dialog).
export async function fetchSampleBody(slug: string, id: string): Promise<string> {
    const res = await fetch(`/api/v2/feed-sources/${slug}/samples/${id}`, {
        credentials: "include",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
}

// ---------- Preview / Promote ----------

export interface PreviewRequest {
    sampleId: string;
    /** Either a parsed mapping JSON object OR a YAML string — backend accepts both. */
    mapping: FeedMapping | string;
}

export interface PreviewRow {
    number: number;
    data: Record<string, unknown>;
}

export interface PreviewResponse {
    rows: PreviewRow[];
    targets: Array<Record<string, unknown>>;
    errors: RunError[];
    dropped: number;
}

export function usePreviewMapping(slug: string) {
    return useMutation({
        mutationFn: (input: PreviewRequest) =>
            api.post<PreviewResponse>(`/api/v2/feed-sources/${slug}/preview`, input),
    });
}

export function usePromoteMapping(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (note?: string) =>
            api.post<{ mappingVersion: number }>(
                `/api/v2/feed-sources/${slug}/promote`,
                note ? { note } : {},
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug] });
        },
    });
}

// ---------- Pause / Resume / Replay ----------

export function usePauseFeed(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.post<{ status: string }>(`/api/v2/feed-sources/${slug}/pause`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug] });
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
        },
    });
}

export function useResumeFeed(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api.post<{ status: string }>(`/api/v2/feed-sources/${slug}/resume`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug] });
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
        },
    });
}

export function useReplayFeed(slug: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () =>
            api.post<{ runId: string; status: string }>(
                `/api/v2/feed-sources/${slug}/replay`,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["feeds", "source", slug] });
        },
    });
}

// ---------- Runs ----------

export function useFeedRuns(slug: string | null | undefined, page = 0, limit = 50) {
    return useQuery({
        queryKey: ["feeds", "source", slug, "runs", page] as const,
        queryFn: () =>
            api
                .get<{ runs: RunSummary[] }>(
                    `/api/v2/feed-sources/${slug}/runs?limit=${limit}&offset=${page * limit}`,
                )
                .then((d) => d.runs ?? []),
        enabled: !!slug,
        staleTime: 5_000,
    });
}

export interface FeedRunDetail {
    run: RunSummary;
    errors: RunError[];
}

export function useFeedRun(slug: string, runId: string | null | undefined) {
    return useQuery({
        queryKey: ["feeds", "source", slug, "run", runId] as const,
        queryFn: () =>
            api.get<FeedRunDetail>(`/api/v2/feed-sources/${slug}/runs/${runId}`),
        enabled: !!runId,
        staleTime: 5_000,
    });
}

// ---------- Default mapping seeds (onboarding) ----------

/**
 * Sensible starter mapping per (connectorKind, formatKind) pair. Used by the
 * wizard banner to pre-populate mapping_draft so the operator sees a rows
 * preview immediately instead of an empty editor.
 *
 * Keep this tight — we're biased toward a mapping that will emit at least one
 * target for the common OSINT shapes (CSV with a `url` column, JSON feeds
 * with `indicator`/`ip`/`domain`, etc.) and let the operator refine.
 */
export function defaultMappingFor(_connectorKind: string, formatKind: string): FeedMapping {
    // CSV — most OSINT CSVs expose `url`, `ip_address`, `domain`, or `hash`.
    if (formatKind === "csv" || formatKind === "tsv") {
        return {
            target: "ioc",
            fields: {
                type: `row.url != "" ? "url" : (row.ip_address != "" ? "ipv4" : (row.domain != "" ? "domain" : "url"))`,
                value: `row.url != "" ? row.url : (row.ip_address != "" ? row.ip_address : row.domain)`,
                tags: `[]`,
            },
        };
    }
    if (formatKind === "json" || formatKind === "ndjson") {
        return {
            target: "ioc",
            fields: {
                type: `row.type != nil ? row.type : "url"`,
                value: `row.indicator != nil ? row.indicator : (row.value != nil ? row.value : row.url)`,
                tags: `[]`,
            },
        };
    }
    if (formatKind === "stix21" || formatKind === "misp") {
        return {
            target: "ioc",
            fields: {
                type: `row.type`,
                value: `row.value`,
                tags: `row.labels`,
            },
        };
    }
    if (formatKind === "regex_lines") {
        return {
            target: "ioc",
            fields: {
                type: `"url"`,
                value: `row.match`,
                tags: `[]`,
            },
        };
    }
    // Fallback — empty but valid.
    return {
        target: "ioc",
        fields: {
            type: `"url"`,
            value: `row.value`,
            tags: `[]`,
        },
    };
}
